import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BackButton } from '@/components/ui/back-button';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, signInWithKakao, loading } = useAuth();
  const [backgroundImage, setBackgroundImage] = useState('');
  
  const redirectTo = searchParams.get('redirectTo') || '/';

  const isProtectedRoute = (path: string) => {
    const protectedPaths = ['/booking', '/payment', '/booking-success', '/booking-history'];
    return protectedPaths.some(protectedPath => path.startsWith(protectedPath));
  };

  const getBackUrl = () => {
    return isProtectedRoute(redirectTo) ? '/' : redirectTo;
  };

  const backgroundImages = useMemo(() => [
    '/images/image1.webp',
    '/images/image2.webp', 
    '/images/image3.webp',
    '/images/image4.webp',
    '/images/image5.webp',
    '/images/image6.webp',
    '/images/image7.webp',
    '/images/image8.webp',
    '/images/image9.webp',
    '/images/image10.webp'
  ], []);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * backgroundImages.length);
    setBackgroundImage(backgroundImages[randomIndex]);
  }, [backgroundImages]);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      // 리다이렉트할 URL이 유효한지 확인
      const targetUrl = redirectTo && redirectTo !== '/auth' ? redirectTo : '/';
      navigate(targetUrl, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, redirectTo]);

  const handleKakaoLogin = async () => {
    try {
      // 로그인 후 리다이렉트할 URL을 설정
      const targetUrl = redirectTo && redirectTo !== '/auth' ? redirectTo : '/';
      await signInWithKakao(`${window.location.origin}${targetUrl}`);
    } catch (error) {
      console.error('Login error:', error);
      toast.error('로그인에 실패했습니다. 다시 시도해주세요.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">리다이렉트 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="auth-page" className="min-h-screen relative flex lg:flex-row overflow-hidden">
      {/* Background Image for Mobile & Desktop Left Side */}
      <div 
        className="absolute inset-0 lg:relative lg:w-1/2 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: backgroundImage ? `url('${backgroundImage}')` : '',
        }}
      >
        <div className="absolute inset-0 bg-black/50 lg:bg-black/40"></div>
        
        {/* Desktop Brand Content */}
        <div className="hidden lg:flex flex-col justify-center items-center h-full relative z-10 px-12 animate-fade-in-left">
          <div className="max-w-lg">
            <img src="/images/logo.svg" alt="홀씨" className="h-16 w-auto mb-8 filter brightness-0 invert" />
            <h1 className="text-display-small lg:text-display-medium font-bold text-white mb-6 leading-tight">
              가슴 뛰는 오늘,
              <span className="block text-primary-300">홀가분하게 떠나는</span>
              나만의 트레킹
            </h1>
            <p className="text-body-large text-white/80 leading-relaxed">
              전문 가이드와 함께하는 안전한 산행,<br />
              리무진 버스로 편안하게 이동하는<br />
              프리미엄 당일 트레킹 투어
            </p>
            
            {/* Decorative elements */}
            <div className="mt-12 flex items-center gap-8 text-white/60">
              <div className="flex items-center gap-2">
                <div className="w-12 h-[1px] bg-white/30"></div>
                <span className="text-caption">Since 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-caption">Premium Trekking</span>
                <div className="w-12 h-[1px] bg-white/30"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <BackButton 
        onClick={() => navigate(getBackUrl())}
        variant="glass" 
        className="absolute top-6 left-6 z-20"
      >
        뒤로
      </BackButton>

      {/* Auth Form Section */}
      <div className="relative z-10 flex items-center justify-center w-full lg:w-1/2 min-h-screen lg:bg-gray-50 py-8">
        <div className="w-11/12 max-w-sm lg:max-w-md animate-scale-in">
          {/* Mobile Card with overlay */}
          <Card className="lg:hidden bg-black/50 backdrop-blur-lg border-white/30 shadow-2xl">
            <CardHeader className="text-center pb-4">
              <Link 
                to="/"
                className="inline-block transition-all duration-300 hover:scale-105 cursor-pointer mb-4"
                aria-label="홈으로 이동"
              >
                <img src="/images/logo.svg" alt="홀씨" className="h-12 w-auto mx-auto filter brightness-0 invert" />
              </Link>
              <CardTitle className="text-h1 text-white">
                홀씨와 함께
                <span className="block text-primary-300">새로운 여행을</span>
              </CardTitle>
              <CardDescription className="text-body-medium text-white/80 mt-2">
                가슴 뛰는 오늘, 홀가분하게 떠나는 나만의 트레킹
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Button 
                data-testid="kakao-login-button"
                onClick={handleKakaoLogin}
                className="w-full bg-[#FEE500] hover:bg-[#FEE500]/90 text-black font-semibold h-14 text-body-large rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] hover:translate-y-[-2px]"
                disabled={loading}
              >
                <img 
                  src="https://developers.kakao.com/assets/img/about/logos/kakaolink/kakaolink_btn_medium.png" 
                  alt="카카오 로고" 
                  className="w-6 h-6 mr-3"
                />
                카카오톡으로 시작하기
              </Button>
              
              <p className="text-center text-body-small text-white/70">
                3초만에 간편하게 시작하세요
              </p>
              
              <div className="text-center">
                <p className="text-caption text-white/60 mb-1">프리미엄 당일 트레킹 투어</p>
                <p className="text-caption text-white/60">리무진 버스로 편안하게, 전문 가이드와 안전하게</p>
              </div>

              <div className="text-center text-caption text-white/50 pt-4 border-t border-white/10">
                로그인하시면 홀씨의 
                <Link to="/terms" className="text-primary-300 hover:text-primary-200 underline mx-1">이용약관</Link>
                및 
                <Link to="/privacy" className="text-primary-300 hover:text-primary-200 underline mx-1">개인정보처리방침</Link>
                에 동의하는 것으로 간주됩니다.
              </div>
            </CardContent>
          </Card>

          {/* Desktop Card */}
          <Card className="hidden lg:block shadow-xl border-gray-200 animate-fade-in-right">
            <CardHeader className="text-center pb-6">
              <Link 
                to="/"
                className="inline-block transition-all duration-300 hover:scale-105 cursor-pointer mb-6"
                aria-label="홈으로 이동"
              >
                <img src="/images/logo.svg" alt="홀씨" className="h-14 w-auto mx-auto" />
              </Link>
              <CardTitle className="text-h1 lg:text-display-small font-bold">
                로그인
              </CardTitle>
              <CardDescription className="text-body-medium text-muted-foreground mt-3">
                홀씨와 함께 특별한 트레킹을 시작하세요
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 px-8">
              <Button 
                data-testid="kakao-login-button"
                onClick={handleKakaoLogin}
                className="w-full bg-[#FEE500] hover:bg-[#FEE500]/90 text-black font-semibold h-14 text-body-large rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] hover:translate-y-[-2px]"
                disabled={loading}
              >
                <img 
                  src="https://developers.kakao.com/assets/img/about/logos/kakaolink/kakaolink_btn_medium.png" 
                  alt="카카오 로고" 
                  className="w-6 h-6 mr-3"
                />
                카카오톡으로 시작하기
              </Button>
              
              <p className="text-center text-body-small text-muted-foreground">
                3초만에 간편하게 시작하세요
              </p>
              
              <div className="text-center pt-6 border-t">
                <p className="text-body-small text-muted-foreground mb-2">처음이신가요?</p>
                <p className="text-caption text-muted-foreground">
                  카카오 계정으로 간편하게 회원가입하고<br />
                  다양한 트레킹 코스를 만나보세요
                </p>
              </div>

              <div className="text-center text-caption text-muted-foreground pt-6 border-t">
                로그인하시면 홀씨의 
                <Link to="/terms" className="text-primary-500 hover:text-primary-600 underline mx-1">이용약관</Link>
                및 
                <Link to="/privacy" className="text-primary-500 hover:text-primary-600 underline mx-1">개인정보처리방침</Link>
                에 동의하는 것으로 간주됩니다.
              </div>
            </CardContent>
          </Card>

          {/* Desktop bottom message */}
          <div className="hidden lg:block text-center mt-8 animate-fade-in">
            <p className="text-body-small text-muted-foreground">
              ✨ 새로운 모험과 특별한 경험이 기다립니다
            </p>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full animate-pulse"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-primary-300/30 rounded-full animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-white/10 rounded-full animate-pulse delay-500"></div>
      </div>
    </div>
  );
};

export default Auth;